import Swal from "sweetalert2";

const showConfirmation = async (type, options) => {
    const {
        entityName = "Record",
        entityId,
        entityIds = [],
        currentStatus,
        action,
        onSuccess,
        onError,
        showCancelledMsg = false,
        customTitle,
        customText,
        customHtml,
        confirmButtonText,
        confirmButtonColor,
        isActivating,
    } = options;

    let result;
    let actionResult = { success: false };

    // ============ DELETE (Hard/Soft) ============
    if (type === "delete") {
        result = await Swal.fire({
            title: customTitle || `Are you sure you want to delete this ${entityName}?`,
            html: customHtml || `
                <p class="mb-3">This action cannot be undone.</p>
                <div class="alert alert-warning bg-warning-subtle border border-warning-subtle text-start p-3 mb-0 text-muted">
                    <div class="mb-2">
                        <strong class="text-danger">Hard Delete</strong>
                        <p class="mb-1">
                            Permanently removes this ${entityName.toLowerCase()} and
                            <strong>all associated data</strong>.
                            This action cannot be reversed.
                        </p>
                    </div>
                    <hr>
                    <div>
                        <strong class="text-primary">Soft Delete</strong>
                        <p class="mb-0">
                            Deactivates the ${entityName.toLowerCase()} while keeping
                            <strong>history accessible</strong>.
                        </p>
                    </div>
                </div>
            `,
            icon: "warning",
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonColor: "#d33",
            denyButtonColor: "#22419e",
            cancelButtonColor: "#777777",
            confirmButtonText: "Hard Delete",
            denyButtonText: "Soft Delete",
            cancelButtonText: "Cancel",
        });

        let deleteType = null;
        if (result.isConfirmed) deleteType = "hard";
        if (result.isDenied) deleteType = "soft";

        if (deleteType) {
            actionResult = await executeAction({
                loadingText: `Deleting ${entityName}...`,
                action: () => action({ deleteId: entityId, deleteType }),
                successTitle: "Deleted!",
                successText: `${entityName} deleted successfully!`,
                entityName,
                onSuccess: (res) => onSuccess && onSuccess(res, deleteType),
                onError,
            });
            actionResult.deleteType = deleteType;
        } else if (result.dismiss === Swal.DismissReason.cancel && showCancelledMsg) {
            showCancelledAlert();
        }
    }

    // ============ MULTIPLE DELETE (Hard/Soft) ============
    else if (type === "multipleDelete") {
        result = await Swal.fire({
            title: customTitle || `Are you sure you want to delete selected ${entityName}s?`,
            html: customHtml || `
                <p class="mb-3">You will not be able to recover these ${entityName.toLowerCase()}s.</p>
                <div class="alert alert-warning bg-warning-subtle border border-warning-subtle text-start p-3 mb-0 text-muted">
                    <div class="mb-2">
                        <strong class="text-danger">Hard Delete</strong>
                        <p class="mb-1">
                            Permanently removes selected ${entityName.toLowerCase()}s and
                            <strong>all their associated data</strong>.
                        </p>
                    </div>
                    <hr>
                    <div>
                        <strong class="text-primary">Soft Delete</strong>
                        <p class="mb-0">
                            Deactivates ${entityName.toLowerCase()}s while preserving
                            <strong>history</strong>.
                        </p>
                    </div>
                </div>
            `,
            icon: "warning",
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonColor: "#d33",
            denyButtonColor: "#22419e",
            cancelButtonColor: "#777777",
            confirmButtonText: "Hard Delete",
            denyButtonText: "Soft Delete",
            cancelButtonText: "Cancel",
        });

        let deleteType = null;
        if (result.isConfirmed) deleteType = "hard";
        if (result.isDenied) deleteType = "soft";

        if (deleteType) {
            actionResult = await executeAction({
                loadingText: `Deleting ${entityName}s...`,
                action: () => action({ selectedRows: entityIds, deleteType }),
                successTitle: "Deleted!",
                successText: `${entityName}s deleted successfully!`,
                entityName,
                onSuccess: (res) => onSuccess && onSuccess(res, deleteType),
                onError,
            });
            actionResult.deleteType = deleteType;
        } else if (result.dismiss === Swal.DismissReason.cancel && showCancelledMsg) {
            showCancelledAlert();
        }
    }

    // ============ SIMPLE DELETE ============
    else if (type === "simpleDelete") {
        result = await Swal.fire({
            title: customTitle || `Are you sure you want to delete this ${entityName}?`,
            text: customText || "You will not be able to recover this record!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#22419e",
            cancelButtonColor: "#777777",
            confirmButtonText: confirmButtonText || "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            actionResult = await executeAction({
                loadingText: `Deleting ${entityName}...`,
                action: () => action(entityId),
                successTitle: "Deleted!",
                successText: `${entityName} deleted successfully!`,
                entityName,
                onSuccess,
                onError,
            });
        } else if (result.dismiss === Swal.DismissReason.cancel && showCancelledMsg) {
            showCancelledAlert();
        }
    }

    // ============ SIMPLE MULTIPLE DELETE ============
    else if (type === "simpleMultipleDelete") {
        result = await Swal.fire({
            title: customTitle || `Are you sure you want to delete selected ${entityName}s?`,
            text: customText || `You will not be able to recover these ${entityName}s!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#22419e",
            cancelButtonColor: "#777777",
            confirmButtonText: confirmButtonText || "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            actionResult = await executeAction({
                loadingText: `Deleting ${entityName}s...`,
                action: () => action(entityIds),
                successTitle: "Deleted!",
                successText: `${entityName}s deleted successfully!`,
                entityName,
                onSuccess,
                onError,
            });
        } else if (result.dismiss === Swal.DismissReason.cancel && showCancelledMsg) {
            showCancelledAlert();
        }
    }

    // ============ TOGGLE STATUS (Activate/Deactivate) ============
    else if (type === "toggleStatus") {
        const isCurrentlyActive = Boolean(currentStatus);
        const willActivate = !isCurrentlyActive; 
        const actionText = willActivate ? "Activate" : "Deactivate";
        const actionTextLower = actionText.toLowerCase();

        result = await Swal.fire({
            title: customTitle || `${actionText} ${entityName}?`,
            text: customText || `Are you sure you want to ${actionTextLower} this ${entityName.toLowerCase()}?`,
            icon: willActivate ? "question" : "warning",
            showCancelButton: true,
            confirmButtonColor: willActivate ? "#28a745" : "#dc3545",
            cancelButtonColor: "#777777",
            confirmButtonText: `Yes, ${actionText}`,
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            actionResult = await executeAction({
                loadingText: `${actionText.replace(/e$/, '')}ing ${entityName}...`,
                action: () => action({ id: entityId, newStatus: willActivate }),
                successTitle: `${actionText}d!`,
                successText: `${entityName} ${actionTextLower}d successfully!`,
                entityName,
                onSuccess: (res) => onSuccess && onSuccess(res, willActivate),
                onError,
            });
            actionResult.newStatus = willActivate;
        } else if (result.dismiss === Swal.DismissReason.cancel && showCancelledMsg) {
            showCancelledAlert();
        }
    }

    // ============ CUSTOM TOGGLE ============
    else if (type === "customToggle") {
        result = await Swal.fire({
            title: customTitle,
            html: customHtml,
            text: customText,
            icon: isActivating ? "question" : "warning",
            showCancelButton: true,
            confirmButtonColor: confirmButtonColor || (isActivating ? "#22419e" : "#dc3545"),
            cancelButtonColor: "#777777",
            confirmButtonText: confirmButtonText,
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            actionResult = await executeAction({
                loadingText: "Processing...",
                action: () => action(entityId, isActivating),
                successTitle: "Success!",
                successText: `${entityName} updated successfully!`,
                entityName,
                onSuccess: (res) => onSuccess && onSuccess(res, isActivating),
                onError,
                showSuccessAlert: false,
            });
            actionResult.newStatus = isActivating;
        }
    }

    return actionResult;
};

/**
 * Execute API Action with Loading & Result Handling
 */
const executeAction = async ({
    loadingText,
    action,
    successTitle,
    successText,
    entityName,
    onSuccess,
    onError,
    showSuccessAlert = true,
}) => {
    try {
        Swal.fire({
            title: "Processing...",
            text: loadingText || "Please wait...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        const res = await action();

        if (showSuccessAlert) {
            await Swal.fire({
                title: successTitle,
                text: res?.message || res?.data?.message || successText,
                icon: "success",
                confirmButtonColor: "#22419e",
            });
        } else {
            Swal.close();
        }

        if (onSuccess) onSuccess(res);

        return { success: true, response: res };

    } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong";

        await Swal.fire({
            title: "Error",
            text: errorMessage,
            icon: "error",
            confirmButtonColor: "#22419e",
        });

        if (onError) onError(error);

        return { success: false, error };
    }
};

/**
 * Show Cancelled Alert
 */
const showCancelledAlert = () => {
    Swal.fire({
        title: "Cancelled",
        text: "Your record is safe",
        icon: "info",
        confirmButtonColor: "#22419e",
    });
};

export default showConfirmation;